import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Optional,
  Inject,
} from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// INTERFACES
import { CustomFieldApiService, SnackBarConfig, trackById } from '../../../shared';
import {
  ECustomFieldType,
  ICustomField,
  ICustomFieldFormValue,
  ICustomFieldValue
} from '../../interfaces';

// SERVICES

interface IDialogData {
  modelId :number;
  modelType :string;
  customFieldValues :ICustomFieldValue[];
  customFieldObject?: string;
}

@Component({
  selector: 'lum-custom-field-form-group',
  templateUrl: './custom-field-form-group.component.html',
  styleUrls: ['./custom-field-form-group.component.scss'],
})
export class CustomFieldFormGroupComponent implements OnInit, OnDestroy {
  @Output()
  onChange = new EventEmitter<string>();

  @Input()
  customFieldValues :ICustomFieldValue[] = [];

  @Input()
  formCustomFieldValues :string;

  @Input()
  customFieldObject ?:string;

  loading$ = new BehaviorSubject<boolean>(false);
  submitting$ = new BehaviorSubject<boolean>(false);

  customFields :ICustomField[] = [];
  customFieldType = ECustomFieldType;
  modelId :number;
  modelType :string;

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private customFieldApiService :CustomFieldApiService,
    private snackbar :MatSnackBar,
    @Optional() public dialogRef :MatDialogRef<CustomFieldFormGroupComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data :IDialogData,
  ) {}

  ngOnInit() :void {
    this.initComponentInputs();
    this.loadCustomFields();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  emitValueChange() :void {
    this.onChange.emit(this.selectedFieldsJSON);
  }

  onSubmit() :void {
    const data = {
      modelType: this.modelType,
      customFieldValues: this.selectedFieldsJSON,
    };

    this.submitting$.next(true);
    this.customFieldApiService
      .saveToModel(this.modelId, data)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.submitting$.next(false)),
      )
      .subscribe({
        next: (response :any) => {
          this.dialogRef.close(response);
        },
        error: (error) => {
          const message = error?.error || 'Error saving custom fields.';

          this.snackbar.open(message, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get isDialog() :boolean {
    return this.dialogRef?.componentInstance === this;
  }

  get selectedFieldsJSON() :string {
    const selectedFields = this.customFields
      .filter((customField) => {
        let value = customField?.value;

        if (typeof value === 'string') {
          value = value.trim();
        }

        return value !== '';
      })
      .map((customField) => ({
        custom_field: customField.id,
        value:
          typeof customField?.value === 'string'
            ? customField?.value.replace(/\s+/g, ' ').trim()
            : customField?.value,
      }));

    return JSON.stringify(selectedFields);
  }

  private loadCustomFields() :void {
    this.loading$.next(true);

    this.customFieldApiService
      .getAll({
        customFieldObject: this.customFieldObject ?? '',
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loading$.next(false);
        }),
      )
      .subscribe({
        next: (customFields :ICustomField[]) => {
          this.customFields = customFields.map((customField) => {
            if (
              this.customFieldValues.length ||
              this.formCustomFieldValues !== undefined
            ) {
              const customFieldValue = this.getCustomFieldValue(customField);

              if (customFieldValue !== null && customFieldValue !== '') {
                return {
                  ...customField,
                  value: customFieldValue,
                };
              }
            }

            return {
              ...customField,
              value: '',
            };
          });

          this.emitValueChange();
        },
        error: () => {
          this.snackbar.open('Error loading custom fields.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getCustomFieldValue(customField :ICustomField) :any {
    if (this.formCustomFieldValues !== undefined) {
      // Validate that formCustomFieldValues is a non-empty string
      if (!this.formCustomFieldValues || typeof this.formCustomFieldValues !== 'string') {
        return '';
      }

      // Attempt to parse JSON only if it looks like valid JSON
      if (!this.formCustomFieldValues.trim().startsWith('[') || !this.formCustomFieldValues.trim().endsWith(']')) {
        return '';
      }

      const customFieldValues = JSON.parse(this.formCustomFieldValues);
      
      // Validate that parsed result is an array
      if (!Array.isArray(customFieldValues)) {
        return '';
      }

      const customFieldValue = customFieldValues.find(
        (customFieldValue :ICustomFieldFormValue) =>
          customFieldValue.custom_field === customField.id,
      );
      return customFieldValue?.value ?? '';
    }

    const customFieldValue = this.customFieldValues.find(
      (customFieldValue) => customFieldValue.custom_field_id === customField.id,
    );

    if (!customFieldValue?.id) {
      return null;
    }

    switch (customField.type) {
      case ECustomFieldType.DROPDOWN:
      case ECustomFieldType.STRING:
        return customFieldValue.value_string;
      case ECustomFieldType.DATE:
        return customFieldValue.value_date;
      case ECustomFieldType.NUMBER:
        return customFieldValue.value_number;
      default:
        return null;
    }
  }

  private initComponentInputs() :void {
    if (
      !this.dialogRef ||
      !(this.dialogRef?.componentInstance instanceof CustomFieldFormGroupComponent)
    ) {
      return;
    }

    if (!this.data) {
      throw new Error('Data must be provided when used as a dialog');
    }

    const { modelId, modelType, customFieldValues, customFieldObject } = this.data;

    if (!modelId || !modelType || !customFieldValues) {
      throw new Error(
        'modelId, modelType, customFieldValues must be provided when used as a dialog',
      );
    }

    this.modelId = modelId;
    this.modelType = modelType;
    this.customFieldValues = customFieldValues;
    this.customFieldObject = customFieldObject || '';
  }
}
